import ExcelJS from 'exceljs'
import { z } from 'zod'

import {
  emailValidation,
  firstAndLastNameValidation,
  middleNameValidation,
  optionalNameValidation,
  countryValidation,
  stateValidation,
  cityValidation,
  newSearchPhone,
  ageValidation,
} from './schemas'

export const batchRowSchema = z.object({
  'report-use-case': z.string().min(1, 'Report use-case is required'),
  'report-type': z.string().min(1, 'Report type is required'),
  'business-state': z.string(),
  'subject-profile-picture-url': z.string().optional(),
  'subject-email': emailValidation,
  'subject-country': countryValidation,
  'subject-state': stateValidation,
  'subject-city': cityValidation,
  'focused-topics': z.string().optional(),
  'subject-first-name': firstAndLastNameValidation,
  'subject-middle-name': middleNameValidation.optional(),
  'subject-last-name': firstAndLastNameValidation,
  'subject-alias': optionalNameValidation.optional(),
  'subject-phone-number': newSearchPhone.optional(),
  'subject-age-approx': ageValidation,
  'subject-employer': z.string().optional(),
  'subject-college': z.string().optional(),
  'subject-facebookUrl': z.string().optional(),
  'subject-twitterUrl': z.string().optional(),
  'subject-linkedinUrl': z.string().optional(),
  'subject-instagramUrl': z.string().optional(),
  'subject-redditUrl': z.string().optional(),
  'subject-pinterestUrl': z.string().optional(),
  'subject-tiktokkUrl': z.string().optional(),
})

export type BatchRow = z.infer<typeof batchRowSchema>

/**
 * Columns a row cannot be uploaded without — the non-optional entries of
 * batchRowSchema above. Everything that flags a missing value reads this, so a
 * field can't end up required in one screen and optional in another.
 */
export const BATCH_REQUIRED_FIELDS = [
  'report-use-case',
  'report-type',
  'subject-email',
  'subject-country',
  'subject-state',
  'subject-first-name',
  'subject-last-name',
]

export interface FieldValidationError {
  field: string
  error: string
}

export interface RowValidationResult {
  rowIndex: number
  isValid: boolean
  data: Record<string, any>
  fieldErrors: FieldValidationError[]
}

export interface BatchValidationResult {
  validRows: BatchRow[]
  errors: { row: number; errors: string[] }[]
  detailedResults: RowValidationResult[]
}

/**
 * Extracts a plain string from an ExcelJS cell value.
 * ExcelJS returns typed objects (hyperlinks, rich text, formulas, etc.)
 * instead of plain strings. This mirrors XLSX's `raw: false` behavior.
 */
function getCellText(value: ExcelJS.CellValue): string {
  if (value == null) return ''
  if (value instanceof Date) return value.toISOString().split('T')[0]
  if (typeof value !== 'object') return String(value)

  // Hyperlink cells: { text: "email@...", hyperlink: "mailto:..." }
  if ('text' in value && 'hyperlink' in value) {
    return String((value as { text: string }).text || '')
  }

  // Rich text cells: { richText: [{ text: "hello", font: {...} }] }
  if ('richText' in value) {
    return (value as { richText: { text: string }[] }).richText
      .map((part) => part.text)
      .join('')
  }

  // Formula cells: { formula: "=A1+B1", result: "value" }
  if ('formula' in value) {
    const result = (value as { formula: string; result?: unknown }).result
    return result != null ? String(result) : ''
  }

  return String(value)
}

function normalizeHeader(header: string): string {
  return header
    .replace(/\*$/, '')
    .trim()
    .replace(/^consent-/, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

export async function parseAndValidateBatchFile(
  file: File | Blob,
  options?: { requireConsentCheck?: (row: any) => boolean },
): Promise<BatchValidationResult> {
  try {
    const arrayBuffer = await file.arrayBuffer()
    const workbook = new ExcelJS.Workbook()
    await workbook.xlsx.load(arrayBuffer)

    const worksheet = workbook.worksheets[0]

    if (!worksheet || worksheet.rowCount < 2) {
      return {
        validRows: [],
        errors: [{ row: 1, errors: ['No data found'] }],
        detailedResults: [],
      }
    }

    // Extract headers from first row
    const headerRow = worksheet.getRow(1)
    const rawHeaders: string[] = []
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      rawHeaders[colNumber - 1] = fixUTF8Encoding(getCellText(cell.value))
    })

    const headerMap: Record<string, string> = {}
    rawHeaders.forEach((h) => {
      headerMap[h] = normalizeHeader(h)
    })

    // Extract data rows (skip header)
    const dataRows: any[][] = []
    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber === 1) return
      const rowArr: any[] = []
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        rowArr[colNumber - 1] = getCellText(cell.value)
      })
      dataRows.push(rowArr)
    })

    const nonEmptyDataRows = dataRows.filter((rowArr) => {
      return rowArr.some((cell) => cell && String(cell).trim() !== '')
    })

    const validRows: BatchRow[] = []
    const errors: { row: number; errors: string[] }[] = []
    const detailedResults: RowValidationResult[] = []

    nonEmptyDataRows.forEach((rowArr, idx) => {
      const row: Record<string, any> = {}
      rawHeaders.forEach((h, i) => {
        const cellValue = rowArr[i] ?? ''
        row[headerMap[h]] = fixUTF8Encoding(String(cellValue))
      })

      const rowIndex = idx + 2
      const fieldErrors: FieldValidationError[] = []

      if (
        row['focused-topics'] &&
        row['focused-topics'].split(/[,;]/).length > 5
      ) {
        fieldErrors.push({
          field: 'focused-topics',
          error: 'Max 5 topics allowed',
        })
      }

      const result = batchRowSchema.safeParse(row)
      const consentRequired = options?.requireConsentCheck?.(row)

      if (consentRequired) {
        if (!row['business-state']) {
          fieldErrors.push({
            field: 'business-state',
            error: 'Business state is required for consent-required use-cases',
          })
        }
      }

      if (!result.success) {
        result.error.errors.forEach((error) => {
          fieldErrors.push({
            field: error.path.join('.'),
            error: error.message,
          })
        })
      }

      const isValid = result.success && fieldErrors.length === 0

      detailedResults.push({
        rowIndex,
        isValid,
        data: row,
        fieldErrors,
      })

      if (isValid) {
        validRows.push(result.data!)
      } else {
        errors.push({
          row: rowIndex,
          errors: fieldErrors.map((fe) => `${fe.field}: ${fe.error}`),
        })
      }
    })

    return { validRows, errors, detailedResults }
  } catch (err) {
    throw err
  }
}

// Helper function to fix common UTF-8 encoding issues
function fixUTF8Encoding(text: string): string {
  if (!text || typeof text !== 'string') return text

  try {
    // Common UTF-8 mojibake patterns and their fixes
    const fixes: Array<[RegExp, string]> = [
      // Portuguese/Spanish accents
      [/SÃ£o/g, 'São'], // São Paulo
      [/ParanÃ¡/g, 'Paraná'], // Paraná
      [/CÃ³rdoba/g, 'Córdoba'], // Córdoba
      [/BrasÃ­lia/g, 'Brasília'], // Brasília
      [/GoiÃ¡s/g, 'Goiás'], // Goiás
      [/CearÃ¡/g, 'Ceará'], // Ceará
      [/MaranhÃ£o/g, 'Maranhão'], // Maranhão
      [/PiauÃ­/g, 'Piauí'], // Piauí
      [/RondÃ´nia/g, 'Rondônia'], // Rondônia
      [/AmapÃ¡/g, 'Amapá'], // Amapá

      // French accents (from previous fix)
      [/Ã\s+le-de-France/g, 'Île-de-France'],
      [/Provence-Alpes-CÃ´te\s+d'/g, "Provence-Alpes-Côte d'"],
      [/Auvergne-RhÃ´ne-Alpes/g, 'Auvergne-Rhône-Alpes'],

      // Common patterns
      [/Ã¡/g, 'á'], // á
      [/Ã¢/g, 'â'], // â
      [/Ã£/g, 'ã'], // ã
      [/Ã¤/g, 'ä'], // ä
      [/Ã¥/g, 'å'], // å
      [/Ã§/g, 'ç'], // ç
      [/Ã¨/g, 'è'], // è
      [/Ã©/g, 'é'], // é
      [/Ãª/g, 'ê'], // ê
      [/Ã«/g, 'ë'], // ë
      [/Ã¬/g, 'ì'], // ì
      [/Ã­/g, 'í'], // í
      [/Ã®/g, 'î'], // î
      [/Ã¯/g, 'ï'], // ï
      [/Ã±/g, 'ñ'], // ñ
      [/Ã²/g, 'ò'], // ò
      [/Ã³/g, 'ó'], // ó
      [/Ã´/g, 'ô'], // ô
      [/Ãµ/g, 'õ'], // õ
      [/Ã¶/g, 'ö'], // ö
      [/Ã¹/g, 'ù'], // ù
      [/Ãº/g, 'ú'], // ú
      [/Ã»/g, 'û'], // û
      [/Ã¼/g, 'ü'], // ü
      [/Ã½/g, 'ý'], // ý
      [/Ã¿/g, 'ÿ'], // ÿ

      // Capital letters
      [/Ã€/g, 'À'], // À
      [/Ã/g, 'Á'], // Á
      [/Ã‚/g, 'Â'], // Â
      [/Ãƒ/g, 'Ã'], // Ã
      [/Ã„/g, 'Ä'], // Ä
      [/Ã…/g, 'Å'], // Å
      [/Ã‡/g, 'Ç'], // Ç
      [/Ãˆ/g, 'È'], // È
      [/Ã‰/g, 'É'], // É
      [/ÃŠ/g, 'Ê'], // Ê
      [/Ã‹/g, 'Ë'], // Ë
      [/ÃŒ/g, 'Ì'], // Ì
      [/Ã/g, 'Í'], // Í
      [/ÃŽ/g, 'Î'], // Î
      [/Ã/g, 'Ï'], // Ï
      [/Ã'/g, 'Ñ'], // Ñ
      [/Ã'/g, 'Ò'], // Ò
      [/Ã"/g, 'Ó'], // Ó
      [/Ã"/g, 'Ô'], // Ô
      [/Ã•/g, 'Õ'], // Õ
      [/Ã–/g, 'Ö'], // Ö
      [/Ã™/g, 'Ù'], // Ù
      [/Ãš/g, 'Ú'], // Ú
      [/Ã›/g, 'Û'], // Û
      [/Ãœ/g, 'Ü'], // Ü
      [/Ã/g, 'Ý'], // Ý
    ]

    let fixedText = text
    fixes.forEach((fix) => {
      const [pattern, replacement] = fix
      fixedText = fixedText.replace(pattern, replacement)
    })

    return fixedText
  } catch (error) {
    console.warn('Error fixing UTF-8 encoding:', error)
    return text
  }
}
